import React from "react";
import { useApp } from "../state";

/* Raydium's product surface, and an honest statement of which parts of it this
 * console drives with your own wallet versus which it hands off to Raydium's
 * own app.
 *
 * Every "native" row below corresponds to a function that exists in this
 * codebase and is exercised by the end-to-end suite. Every "link out" row says
 * why, in terms of what would have to be true for it to be safe to ship here —
 * not "coming soon". A console that claimed coverage it did not have would be
 * worse than one that covers less.
 *
 * Product names and links checked against Raydium's own documentation and app:
 *   docs.raydium.io  ·  raydium.io  ·  perps.raydium.io
 */

type Status = "native" | "partial" | "external" | "not-raydium";

interface Row {
  product: string;
  what: string;
  status: Status;
  /** Where it lives here, or why it doesn't. */
  detail: string;
  /** Tab in this console, when native. */
  here?: string;
  href?: string;
  hrefLabel?: string;
}

const ROWS: Row[] = [
  {
    product: "CPMM — create pool",
    what: "Constant-product pool, no OpenBook market required",
    status: "native",
    here: "Launch",
    detail:
      "raydium.cpmm.createPool, paired against wrapped SOL, with the fee tier read live off the chain and every field of the resulting pool read back and compared.",
  },
  {
    product: "CPMM — swap",
    what: "Trade against your pool",
    status: "native",
    here: "Swap",
    detail:
      "raydium.cpmm.swap. The wallet balance delta is measured after the transaction succeeds, because the SDK's quote subtracts a creator fee this pool type does not charge.",
  },
  {
    product: "CPMM — add / withdraw liquidity",
    what: "Deposit or remove both sides of the pair",
    status: "native",
    here: "Liquidity",
    detail: "raydium.cpmm.addLiquidity and withdrawLiquidity, with slippage you set.",
  },
  {
    product: "Burn & Earn — lock LP",
    what: "Permanently lock liquidity, keep the fee stream",
    status: "native",
    here: "Burn & LP",
    detail:
      "raydium.cpmm.lockLp. LP tokens go to the Burn & Earn locker program and cannot be withdrawn; you receive a transferable Fee Key NFT that carries the right to the fees.",
    href: "https://docs.raydium.io/raydium/liquidity-tools/burn-and-earn",
    hrefLabel: "Raydium docs",
  },
  {
    product: "Burn & Earn — harvest fees",
    what: "Claim fees accrued to locked liquidity",
    status: "native",
    here: "Burn & LP",
    detail:
      "raydium.cpmm.harvestLockLp against your Fee Key NFT. Without this the lock is a one-way door with the earnings left behind it.",
  },
  {
    product: "CPMM — creator fees",
    what: "Collect the pool's creator fee balance",
    status: "native",
    here: "Burn & LP",
    detail:
      "raydium.cpmm.collectCreatorFees. Note that pools made with createPool have enableCreatorFee = false and accrue nothing — the Dashboard shows the live balance so you can see that for yourself.",
  },
  {
    product: "CPMM — pool and fee-tier state",
    what: "Reserves, config, authorities, creator-fee balances",
    status: "native",
    here: "Dashboard",
    detail: "Read straight from RPC. No indexer, no API, nothing cached.",
  },
  {
    product: "Swap (routed)",
    what: "Best-price routing across every Raydium pool",
    status: "external",
    detail:
      "The swap here goes directly against one pool you specify. Routed swaps price across pools this console never loads, and a router that silently mis-routes is worse than no router.",
    href: "https://raydium.io/swap/",
    hrefLabel: "raydium.io/swap",
  },
  {
    product: "AMM v4 (Standard, OpenBook)",
    what: "The legacy hybrid AMM",
    status: "external",
    detail:
      "Pool creation needs an OpenBook market ID. Raydium's own app creates that market for you; nothing here does, and a half-created market is an expensive mistake to leave a user holding.",
    href: "https://raydium.io/liquidity/create-pool/",
    hrefLabel: "raydium.io create pool",
  },
  {
    product: "CLMM (Concentrated)",
    what: "Liquidity concentrated into a price range",
    status: "external",
    detail:
      "Positions are NFTs over tick ranges, with their own rebalancing and impermanent-loss profile. It is a different product, not a setting on this one.",
    href: "https://raydium.io/clmm/create-pool/",
    hrefLabel: "raydium.io CLMM",
  },
  {
    product: "Farms / staking",
    what: "Reward emissions to liquidity providers",
    status: "external",
    detail:
      "A farm commits you to funding a reward stream over time. That belongs somewhere its schedule and remaining balance are visible, which is Raydium's own interface.",
    href: "https://docs.raydium.io/raydium/liquidity-providers/liquidity-pools/raydium-farms",
    hrefLabel: "Raydium docs",
  },
  {
    product: "LaunchLab",
    what: "Bonding-curve launches that graduate to a pool",
    status: "external",
    detail:
      "A different launch model from this console's: supply is sold along a curve rather than minted in full and paired with your own SOL. Choose one deliberately.",
    href: "https://raydium.io/launchpad/",
    hrefLabel: "raydium.io/launchpad",
  },
  {
    product: "Perps",
    what: "Perpetual futures via Orderly Network",
    status: "external",
    detail:
      "An off-chain order book with its own Ed25519 session signing. Nothing to do with issuing a token, and not something to bolt onto a wallet console.",
    href: "https://perps.raydium.io/",
    hrefLabel: "perps.raydium.io",
  },
  {
    product: "Portfolio",
    what: "All your positions across every pool type",
    status: "partial",
    here: "Dashboard",
    detail:
      "The Dashboard shows the one token and pool this console is working on, in full. It does not enumerate your other positions — that needs the pool types above.",
    href: "https://raydium.io/portfolio/",
    hrefLabel: "raydium.io/portfolio",
  },
  {
    product: "Burn tokens (SPL)",
    what: "Destroy supply you hold",
    status: "not-raydium",
    here: "Burn & LP",
    detail:
      "An SPL Token program instruction, not a Raydium feature. Included because a launch usually ends with one.",
  },
  {
    product: "Batch distribution",
    what: "Send to up to 500 wallets from a CSV",
    status: "not-raydium",
    here: "Distribute",
    detail:
      "Also not Raydium. Plain SPL transfers, batched, each batch verified before the next is signed.",
  },
];

const STATUS_PILL: Record<Status, { cls: string; label: string }> = {
  native: { cls: "ok", label: "In this console" },
  partial: { cls: "warn", label: "Partly" },
  external: { cls: "", label: "Raydium app" },
  "not-raydium": { cls: "", label: "Not Raydium" },
};

export default function Coverage() {
  const { net } = useApp();
  const native = ROWS.filter((r) => r.status === "native").length;
  const raydiumRows = ROWS.filter((r) => r.status !== "not-raydium");

  return (
    <>
      <div className="card">
        <h2>Raydium feature coverage</h2>
        <p className="sub">
          {native} of Raydium's {raydiumRows.length} surfaces are driven here with your own wallet.
          The rest open Raydium's app, and each row says why rather than promising it later.
        </p>
        <div className="scroll" style={{ maxHeight: "none" }}>
          <table data-testid="coverage-table">
            <thead>
              <tr>
                <th>Raydium surface</th>
                <th>Where</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => {
                const p = STATUS_PILL[r.status];
                return (
                  <tr key={r.product} data-status={r.status}>
                    <td>
                      <strong>{r.product}</strong>
                      <div style={{ color: "var(--muted)" }}>{r.what}</div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <span className={`pill ${p.cls}`}>{p.label}</span>
                      {r.here ? (
                        <div style={{ color: "var(--muted)", marginTop: 4 }}>{r.here} tab</div>
                      ) : null}
                    </td>
                    <td>
                      {r.detail}
                      {r.href ? (
                        <div style={{ marginTop: 4 }}>
                          <a href={r.href} target="_blank" rel="noreferrer noopener">
                            {r.hrefLabel ?? r.href} ↗
                          </a>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <h2>What it takes to mint a coin</h2>
        <p className="sub">
          The Launch tab marks every field, but the short version is this.
        </p>
        <h3>Required — no token exists without them</h3>
        <ul>
          <li>
            <strong>Name</strong> and <strong>symbol</strong> — 1–32 and 1–10 characters, written
            into the Metaplex metadata account.
          </li>
          <li>
            <strong>Decimals</strong> — 0 to 9, fixed permanently at creation.
          </li>
          <li>
            <strong>Total supply</strong> — minted once, in full, to your wallet.
          </li>
          <li>
            <strong>Metadata JSON URI</strong> — https://, ipfs:// or ar://. Skip it and the token
            has no name or image anywhere off-chain.
          </li>
          <li>
            <strong>A connected wallet holding SOL</strong> — pays rent and fees and signs each
            step. Roughly 0.02 SOL for a token on its own.
          </li>
        </ul>
        <h3>Required only if you are also opening a pool</h3>
        <ul>
          <li>
            <strong>Tokens and SOL to deposit</strong> — together these set the opening price.
          </li>
          <li>
            <strong>Fee tier</strong> — read live from {net.label}. Never selected by index: index
            N is a different tier on devnet than on mainnet.
          </li>
        </ul>
        <h3>Optional, and permanent once done</h3>
        <ul>
          <li>
            <strong>Revoking mint authority</strong> — fixes supply forever.
          </li>
          <li>
            <strong>Revoking freeze authority</strong> — nobody can ever freeze a holder's account.
          </li>
          <li>
            <strong>Making metadata immutable</strong> — name, symbol and URI can never change, so
            a dead URI stays dead.
          </li>
        </ul>
        <p className="sub" style={{ marginTop: 12 }}>
          Order matters and is enforced: metadata is written before any authority is revoked.
          Reversed, the Metaplex program rejects the metadata transaction and the token is
          permanently nameless.
        </p>
      </div>
    </>
  );
}
